import useMediaQuery from '@mui/material/useMediaQuery';
import {DatePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import {useTheme} from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

const getMaxRows = (sections) => {
    return sections
        ? Math.max(...sections.map((s) => s.length))
        : 0;
};

export const EditableFields = ({fields, edit, sections}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const renderByType = ({textFieldProps, otherProps}) => ({
        select: () => (
            <TextField
                sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    }
                }}
                key={`select_${textFieldProps.name}`}
                variant="outlined"
                margin="dense"
                fullWidth
                {...textFieldProps}
            >
                {otherProps.options
                    ? otherProps.options
                        .map(x => (
                            <MenuItem
                                key={x.value}
                                value={x.value}
                            >
                                {x.title}
                            </MenuItem>
                        ))
                    : <MenuItem/>
                }
            </TextField>
        ),
        date: () => (
            <DatePicker
                sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    }
                }}
                key={`select_${textFieldProps.name}`}
                format={'D-MMM-YYYY'}
                maxDate={textFieldProps.maxDate || moment()}
                minDate={textFieldProps.minDate || moment()}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        name: textFieldProps.name,
                        margin: 'dense',
                        required: textFieldProps.required
                    },
                    field: {
                        readOnly: true
                    }
                }}
                {...{
                    ...textFieldProps,
                    onChange: value => textFieldProps.onChange({target: {value, name: textFieldProps.name}}, {})
                }}
            />
        ),
        text: () => (
            <TextField
                sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    }
                }}
                key={`textField_${textFieldProps.name}`}
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{
                    style: {overflow: 'visible'},
                }}
                {...textFieldProps}
            />
        )
    })[textFieldProps.type ?? 'text']();

    const renderFields = (fields) =>
        fields.map(({textFieldProps, otherProps}, rowIndex) => (
            edit
                ? <Grid
                    item
                    xs={12}
                    key={`field-${rowIndex}`}
                >
                    {renderByType({textFieldProps, otherProps})}
                </Grid>
                : <Grid
                    item
                    container
                    key={`readonly-field-${rowIndex}`}
                    xs={12}
                    paddingTop={2}
                    paddingBottom={2}
                    height={'100%'}
                    sx={{alignItems: 'flex-start'}}
                    borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
                >
                    <Grid item xs={4}>
                        <Typography variant="body2" fontWeight="bold">{textFieldProps.label}</Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <Typography variant="body2" fontWeight="bold">:</Typography>
                    </Grid>
                    <Grid item xs={7}>
                        {(() => {
                            const displayValue = textFieldProps.type === 'date'
                                ? textFieldProps.value != null && textFieldProps.value !== ''
                                    ? textFieldProps.value.format('DD-MMM-YYYY')
                                    : ''
                                : textFieldProps.type === 'select'
                                    ? otherProps.options?.find(x => x.value === textFieldProps.value)?.title
                                    : textFieldProps.value;

                            return displayValue
                                ? (
                                    <Tooltip title={displayValue} arrow>
                                        <Box
                                            sx={{
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minWidth: 0,
                                                cursor: 'default'
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '100%'
                                                }}
                                            >
                                                {displayValue}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )
                                : (
                                    <Typography variant="body2">
                                        {displayValue}
                                    </Typography>
                                );
                        })()}
                    </Grid>
                </Grid>
        ));

    return (
        sections
            ? <Grid container>
                {
                    Array.from({length: getMaxRows(sections)}).map((_, rowIndex) => (
                        <Grid
                            item
                            container
                            direction="row"
                            sx={{
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                            }}
                            key={`row-${rowIndex}`}
                            columnSpacing={2}
                        >
                            {
                                sections?.map((section, sectionIndex) => {
                                    const cell = section[rowIndex];

                                    return !cell && isSmallScreen
                                        ? null
                                        : (
                                            <Grid
                                                item
                                                xs={12}
                                                md={12 / sections.length}
                                                key={`rowItem-${sectionIndex}`}
                                                height={isSmallScreen ? 'auto' : '100%'}
                                            >
                                                {
                                                    cell ? renderFields([cell]) : null
                                                }
                                            </Grid>
                                        );
                                })
                            }
                        </Grid>
                    ))
                }
            </Grid>
            : <Grid container>
                {renderFields(Object.values(fields))}
            </Grid>
    );
};

EditableFields.propTypes = {
    fields: PropTypes.object,
    sections: PropTypes.array,
    edit: PropTypes.bool
};